/**
 * Shared theme variants for subpages (/uslugi, /o-nas, /kontakt).
 *
 * Design principle: subpages inherit the visual language of the homepage
 * Services section — warm stone surfaces in light mode, rich zinc in dark mode,
 * with consistent orange accents throughout.
 *
 *   Surface   →  stone-100 / zinc-900   (matches Services section bg)
 *   Cards     →  white→orange-50 / zinc-800/60  (matches serviceCard)
 *   Accents   →  orange-600 / orange-400 (unified, no blue in dark)
 */

export const pageTheme = {
  // ─── Page Wrapper ─────────────────────────────────────────────────────────────
  wrapper:
    "relative flex flex-col w-full bg-stone-100 dark:bg-zinc-900 min-h-screen pt-32 pb-24 overflow-hidden",
  wrapperContact:
    "relative flex flex-col w-full bg-stone-100 dark:bg-zinc-900 min-h-[calc(100vh-80px)] pt-32 pb-12 lg:pb-24 overflow-hidden",

  // ─── Ambient Orbs ─────────────────────────────────────────────────────────────
  orbLeft:
    "absolute top-1/4 -left-64 h-[600px] w-[600px] rounded-full dark:opacity-5 opacity-30 blur-[150px] pointer-events-none bg-orange-300 dark:bg-orange-600 dark:opacity-20 z-0",
  orbRight:
    "absolute bottom-1/3 -right-64 h-[500px] w-[500px] rounded-full dark:opacity-5 opacity-20 blur-[150px] pointer-events-none bg-orange-400 dark:bg-orange-500 dark:opacity-15 z-0",

  // ─── Section Cards ────────────────────────────────────────────────────────────
  card:
    "bg-white dark:bg-zinc-800/60 border border-stone-200/60 dark:border-zinc-700/50 shadow-xl hover:shadow-2xl transition-all rounded-4xl overflow-hidden",

  // ─── Tag Badges ───────────────────────────────────────────────────────────────
  tagBadge:
    "inline-flex items-center gap-2 rounded-full border border-orange-500/30 bg-orange-500/10 px-4 py-1.5 text-sm font-semibold text-orange-700 dark:text-orange-400",

  // ─── Icon Containers ──────────────────────────────────────────────────────────
  iconContainer:
    "flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-orange-100 dark:bg-orange-950/40 text-orange-600 dark:text-orange-500",

  // ─── Typography ───────────────────────────────────────────────────────────────
  pageTag:
    "text-orange-600 dark:text-orange-500 font-bold tracking-widest uppercase text-sm mb-3",
  pageTitle:
    "text-4xl font-extrabold tracking-tight text-zinc-900 dark:text-zinc-100 sm:text-6xl text-balance",
  pageDesc:
    "mt-6 text-lg sm:text-xl leading-8 text-zinc-600 dark:text-zinc-400",
  sectionTitle:
    "text-3xl font-bold text-zinc-900 dark:text-white mb-4",
  bodyText:
    "text-zinc-600 dark:text-zinc-400",
  headingText:
    "text-zinc-900 dark:text-zinc-100",

  // ─── Accent Links ─────────────────────────────────────────────────────────────
  accentLink:
    "text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 transition-colors",

  // ─── Contact Page Card ────────────────────────────────────────────────────────
  contactCard:
    "flex flex-col lg:flex-row w-full bg-white dark:bg-zinc-800/60 rounded-4xl overflow-hidden shadow-xl border border-stone-200/60 dark:border-zinc-700/50 max-w-7xl mx-auto",
  contactMapBg:
    "bg-stone-200 dark:bg-zinc-700/50",

  // ─── Pillar / Bento Cards ─────────────────────────────────────────────────────
  pillarCard:
    "group relative bg-white dark:bg-zinc-800/60 p-8 rounded-4xl border border-stone-200/60 dark:border-zinc-700/50 shadow-lg hover:shadow-2xl hover:border-orange-500/30 dark:hover:border-orange-500/20 backdrop-blur-sm transition-all overflow-hidden",
  pillarIcon:
    "h-12 w-12 rounded-2xl bg-orange-50 dark:bg-orange-950/30 flex items-center justify-center text-orange-600 dark:text-orange-400 group-hover:scale-110 transition-transform",

  // ─── Partnership Section ──────────────────────────────────────────────────────
  partnershipBg:
    "py-24 bg-stone-50 dark:bg-zinc-800/30 border-y border-stone-200/60 dark:border-zinc-700/50",
  partnerCard:
    "flex flex-col md:flex-row items-center gap-8 bg-white dark:bg-zinc-800/60 p-8 rounded-4xl border border-stone-200/60 dark:border-zinc-700/50 shadow-xl hover:shadow-orange-500/10 hover:border-orange-500/30 transition-all group",
  partnerLogoBg:
    "w-32 h-32 shrink-0 flex items-center justify-center p-4 bg-stone-50 dark:bg-zinc-800/40 rounded-3xl group-hover:scale-110 transition-transform",
  partnerBadge:
    "inline-flex px-3 py-1 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-500 text-xs font-bold uppercase tracking-wider mb-2",

  // ─── Timeline ─────────────────────────────────────────────────────────────────
  timelineCard:
    "bg-white dark:bg-zinc-800/60 p-5 md:p-6 rounded-4xl border border-stone-200/60 dark:border-zinc-700/50 shadow-xl hover:shadow-2xl transition-all group overflow-hidden",
  timelineYearBubble:
    "absolute left-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold z-10 shadow-lg border-4 border-stone-100 dark:border-zinc-900 hidden md:flex",
  timelineIconBg:
    "p-2 md:p-2.5 rounded-2xl bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-500",

  // ─── Bullet Icons ─────────────────────────────────────────────────────────────
  bulletIcon:
    "flex h-6 w-6 mt-0.5 shrink-0 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400 shadow-sm",

  // ─── Image Frames ─────────────────────────────────────────────────────────────
  imageFrame:
    "relative aspect-video lg:aspect-square w-full sm:w-[500px] lg:w-full mx-auto rounded-4xl overflow-hidden shadow-2xl bg-white dark:bg-zinc-800/60 ring-1 ring-stone-200/60 dark:ring-zinc-700/50 p-2 md:p-3",
  imagePlaceholder:
    "absolute inset-2 md:inset-3 rounded-3xl bg-stone-50 dark:bg-zinc-800/50 flex items-center justify-center border border-dashed border-stone-300 dark:border-zinc-700",
};
